import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import PayNowButton from "./PayNowButton";

const ProductDetail = ({ product, onBack }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(product.sizes[0]);
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const handleAddToCart = () => {
    addToCart(product, selectedSize, 1);
    // Show success message
    alert(`${product.name} added to cart!`);
  };

  const handleWishlistToggle = () => {
    if (isInWishlist(product._id)) {
      removeFromWishlist(product._id);
      alert(`${product.name} removed from wishlist!`);
    } else {
      addToWishlist(product);
      alert(`${product.name} added to wishlist!`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Products
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Gallery */}
        <div>
          <div className="mb-4 relative">
            {product.images && product.images.length > 0 ? (
              <>
                <img 
                  src={product.images[currentImage] ? product.images[currentImage] : '/images/placeholder.jpg'} 
                  alt={product.name} 
                  className="w-full h-96 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
                {product.images.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white text-sm px-3 py-1 rounded-full">
                    {currentImage + 1} of {product.images.length}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>No images available</p>
                </div>
              </div>
            )}
          </div>
          {product.images && product.images.length > 1 && (
            <div className="flex space-x-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${
                    index === currentImage ? 'border-pink-600' : 'border-gray-300'
                  }`}
                  onClick={() => setCurrentImage(index)}
                >
                  <img 
                    src={image} 
                    alt={`${product.name} ${index + 1}`} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/images/placeholder.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>
          
          <div className="flex items-center mb-4">
            <span className="text-3xl font-bold text-gray-900">₹{product.price}</span>
            {product.original_price && (
              <span className="ml-3 text-xl text-gray-500 line-through">₹{product.original_price}</span>
            )}
          </div>
          <p className="text-gray-600 mb-4">Inclusive of all taxes</p>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Product Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><strong>Material:</strong> {product.material}</div>
              <div><strong>Pattern:</strong> {product.pattern}</div>
              <div><strong>Color:</strong> {product.color}</div>
              <div><strong>Occasion:</strong> {product.occasion}</div>
              {product.length && <div><strong>Length:</strong> {product.length}</div>}
              {product.sleeve_type && <div><strong>Sleeve:</strong> {product.sleeve_type}</div>}
              {product.neck_type && <div><strong>Neck:</strong> {product.neck_type}</div>}
              <div><strong>Fabric:</strong> {product.fabric}</div>
              <div><strong>Wash Care:</strong> {product.wash_care}</div>
              <div><strong>Silhouette:</strong> {product.silhouette}</div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Size Guide</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">Size</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Bust</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Waist</th>
                  </tr>
                </thead>
                <tbody>
                  {product.size_guide.map((size, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2">{size.size}</td>
                      <td className="border border-gray-300 px-4 py-2">{size.bust}</td>
                      <td className="border border-gray-300 px-4 py-2">{size.waist}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Size</label>
            <select 
              className="border border-gray-300 rounded-lg px-4 py-2 w-full"
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
            >
              {product.sizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="flex space-x-4 mb-4">
            <button 
              onClick={handleAddToCart}
              className="flex-1 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Add to Cart
            </button>
            <button 
              onClick={handleWishlistToggle}
              className={`border px-6 py-3 rounded-lg transition-colors ${
                isInWishlist(product._id) 
                  ? 'border-pink-300 bg-pink-50 text-pink-600 hover:bg-pink-100' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              title={isInWishlist(product._id) ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <svg className={`w-5 h-5 ${isInWishlist(product._id) ? 'fill-current' : ''}`} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>
          
          {/* Pay Now Button */}
          <div className="mb-6">
            <PayNowButton product={product} className="w-full" />
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Description</h3>
            <p className="text-gray-700">{product.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
